import { useQuery } from '@tanstack/react-query';
import { useLatest, useToggle } from 'ahooks';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { queries } from 'services/queries';

const useTranslationToggle = (
  text: string | null | undefined,
  defaultMap: Record<General.Language | 'en' | 'zh', string>,
  reporter?: (
    translatedText: string,
    targetLanguage: string,
    fullTranslationMap: Record<General.Language | 'en' | 'zh', string>,
  ) => void, // 翻译成功后执行上报的动作
  showTrans?: boolean,
) => {
  const translateMapRef = useRef({ ...defaultMap });
  const reporterRef = useLatest(reporter);

  const [displayTranslation, { setLeft: showOrigin, setRight: showTranslation }] = useToggle(false);
  const lang = useSystemLanguageValue();

  // 判断是否需要翻译：有文本且当前语言没有缓存
  const needTranslate = !!text && translateMapRef.current[lang] === undefined;

  const {
    data: translatedText,
    isLoading,
    error,
    refetch,
  } = useQuery({
    ...queries.translates.overseaTranslate(text!, lang, 'side'),
    enabled: needTranslate,
  });

  const pending = useMemo(() => {
    return needTranslate ? isLoading : false;
  }, [isLoading, needTranslate]);

  // 翻译成功后更新缓存并执行回调
  useEffect(() => {
    if (translatedText) {
      translateMapRef.current[lang] = translatedText;
      reporterRef.current?.(translatedText, lang, translateMapRef.current);
      if (showTrans) {
        showTranslation();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [translatedText, lang, showTrans, showTranslation]);

  useEffect(() => {
    if (showTrans && translateMapRef.current[lang] !== undefined) {
      showTranslation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang, showTrans, showTranslation]);

  const displayText = useMemo(() => {
    return !pending && !error && displayTranslation ? translateMapRef.current[lang] : text;
  }, [pending, error, displayTranslation, translateMapRef, lang, text]);

  // 手动触发翻译（当缓存已存在时可用于强制重新翻译）
  const doTrans = useCallback(() => {
    if (!text) return;
    refetch();
  }, [text, refetch]);

  return { displayText, pending, error, doTrans, displayTranslation, showOrigin, showTranslation };
};

export default useTranslationToggle;
